import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Loader2,
  AlertCircle,
  Battery as BatteryIcon,
  Zap,
  User as UserIcon,
  Cpu,
  Lightbulb,
  SlidersHorizontal,
  ChevronLeft,
  ChevronRight,
  PlugZap,
  Download,
  X,
} from 'lucide-react';
import {
  downloadSiteCsv,
  getSite,
  getEnergyHistory,
  getEnergyFlowForDay,
  getBatteryModeEvents,
  getEVChargers,
  getEVChargerSessions,
  type EnergyHistoryResponse,
  type BatteryModeEvent,
  type EVChargingSession,
  type CsvExportKind,
} from '../api/client';
import { EnergyFlowChart } from '../components/site';
import type { SiteDetail as SiteDetailType, EnergyFlowDataPoint } from '../types';

const DEFAULT_DAYS = 30;
// Battery mode log scope: only show changes from the recent past, paginated
// so the page doesn't get dominated by historical events when the
// controller's been busy. 48 h is enough to cover yesterday's full
// optimization cycle plus today so far.
const MODE_LOG_HOURS = 48;
const MODE_LOG_PAGE_SIZE = 5;
// EV charging sessions: how far back the table looks.
const EV_SESSION_DAYS = 30;

function parseUtc(value: string): Date {
  // API timestamps are naive UTC ("2026-07-08T12:00:00"); pin the zone.
  return new Date(/[zZ]|[+-]\d\d:\d\d$/.test(value) ? value : `${value}Z`);
}

function formatSessionDuration(startedAt: string, endedAt: string | null): string {
  if (!endedAt) return 'in progress';
  const ms = parseUtc(endedAt).getTime() - parseUtc(startedAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const mins = Math.round(ms / 60000);
  if (mins < 60) return `${mins} min`;
  return `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;
}

function formatDate(yyyymmdd: string): string {
  // YYYY-MM-DD (site-local) → "Mon, 5 May". We split the parts manually
  // so the label is tz-agnostic (the API has already done the local-day
  // bucketing for the site's timezone).
  const [y, m, d] = yyyymmdd.split('-').map((s) => Number.parseInt(s, 10));
  if (!y || !m || !d) return yyyymmdd;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

function formatTimestamp(ts: string): string {
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return ts;
  return d.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatCurrency(value: number): string {
  const sign = value < 0 ? '+' : value > 0 ? '-' : '';
  return `${sign}$${Math.abs(value).toFixed(2)}`;
}

function netCostClass(value: number): string {
  if (value > 0) return 'text-red-600 dark:text-red-400';
  if (value < 0) return 'text-green-600 dark:text-green-400';
  return 'text-gray-600 dark:text-gray-300';
}

export function EnergyHistory() {
  const { siteId } = useParams<{ siteId: string }>();
  const [site, setSite] = useState<SiteDetailType | null>(null);
  const [history, setHistory] = useState<EnergyHistoryResponse | null>(null);
  const [modeEvents, setModeEvents] = useState<BatteryModeEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modeLogPage, setModeLogPage] = useState(0);
  // EV charging sessions for the site's charger (null = site has no charger).
  const [evSessions, setEvSessions] = useState<EVChargingSession[] | null>(null);
  const [evChargerName, setEvChargerName] = useState<string>('EV Charger');

  // Half-hour breakdown for one day, driven by clicking a daily-totals row
  // (or the prev/next controls on the chart). `selectedDate` is a
  // site-local YYYY-MM-DD from the history rollup.
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [dayFlow, setDayFlow] = useState<EnergyFlowDataPoint[]>([]);
  const [dayLoading, setDayLoading] = useState(false);
  const [dayError, setDayError] = useState<string | null>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  // Drop events older than the cutoff and recompute on every fetch.
  // Memoised so pagination doesn't re-filter on every render.
  const recentModeEvents = useMemo(() => {
    const cutoffMs = Date.now() - MODE_LOG_HOURS * 60 * 60 * 1000;
    return modeEvents.filter((event) => {
      const t = new Date(event.timestamp).getTime();
      return Number.isFinite(t) && t >= cutoffMs;
    });
  }, [modeEvents]);

  const totalPages = Math.max(1, Math.ceil(recentModeEvents.length / MODE_LOG_PAGE_SIZE));
  // Clamp page if filter shrinks below current page (e.g. on refresh).
  const safePage = Math.min(modeLogPage, totalPages - 1);
  const pagedModeEvents = recentModeEvents.slice(
    safePage * MODE_LOG_PAGE_SIZE,
    safePage * MODE_LOG_PAGE_SIZE + MODE_LOG_PAGE_SIZE,
  );

  useEffect(() => {
    if (!siteId) return;
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const [siteResp, historyResp, modeResp] = await Promise.all([
          getSite(siteId!),
          getEnergyHistory(siteId!, DEFAULT_DAYS),
          getBatteryModeEvents(siteId!, 200),
        ]);
        if (cancelled) return;
        setSite(siteResp);
        setHistory(historyResp);
        setModeEvents(modeResp);
        // Open on the most recent day so the chart has something to show
        // (and the paging controls are usable) without a click.
        setSelectedDate((current) => current ?? historyResp.days[0]?.date ?? null);
        // EV sessions are optional: most sites have no charger, and a
        // failure here must not take the whole history page down.
        try {
          const chargers = await getEVChargers(siteId!);
          if (cancelled) return;
          const charger = chargers[0];
          if (charger) {
            setEvChargerName(charger.name || 'EV Charger');
            const rows = await getEVChargerSessions(siteId!, charger.id, EV_SESSION_DAYS);
            if (cancelled) return;
            setEvSessions(rows);
          } else {
            setEvSessions(null);
          }
        } catch (evErr) {
          console.error('Failed to load EV charging sessions:', evErr);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load energy history:', err);
        setError(err instanceof Error ? err.message : 'Failed to load energy history');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [siteId]);

  useEffect(() => {
    if (!siteId || !selectedDate) return;
    let cancelled = false;

    async function loadDay() {
      setDayLoading(true);
      setDayError(null);
      try {
        const resp = await getEnergyFlowForDay(siteId!, selectedDate!);
        if (cancelled) return;
        setDayFlow(resp.intervals || []);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load energy flow for day:', err);
        setDayFlow([]);
        setDayError(err instanceof Error ? err.message : 'Failed to load day');
      } finally {
        if (!cancelled) setDayLoading(false);
      }
    }

    loadDay();
    return () => {
      cancelled = true;
    };
  }, [siteId, selectedDate]);

  const selectDay = (date: string) => {
    setSelectedDate(date);
    chartRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-brand-orange" />
      </div>
    );
  }

  if (error || !site || !history) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="w-10 h-10 text-red-500 dark:text-red-400 mx-auto" />
        <p className="mt-4 text-red-500 dark:text-red-400">
          {error || 'Energy history not available'}
        </p>
        <Link
          to={siteId ? `/site/${siteId}` : '/'}
          className="mt-4 inline-flex items-center gap-1 text-sm text-brand-orange hover:underline"
        >
          <ArrowLeft className="w-4 h-4" /> Back to site
        </Link>
      </div>
    );
  }

  const days = history.days;
  const summary = history.summary;
  const daysWithData = summary.days_with_data;

  // `days` is newest-first, so "previous" (older) is the next index.
  const selectedIdx = selectedDate ? days.findIndex((d) => d.date === selectedDate) : -1;
  const olderDay = selectedIdx >= 0 ? days[selectedIdx + 1] : undefined;
  const newerDay = selectedIdx > 0 ? days[selectedIdx - 1] : undefined;
  const selectedRow = selectedIdx >= 0 ? days[selectedIdx] : undefined;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="card p-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <Link
              to={`/site/${siteId}`}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
              title="Back to site"
            >
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Energy History
              </h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {site.name} · Last {DEFAULT_DAYS} days
                {history.timezone && (
                  <span className="ml-1 text-gray-400 dark:text-gray-500">
                    ({history.timezone})
                  </span>
                )}
                {daysWithData < DEFAULT_DAYS && (
                  <span className="ml-1">
                    · {daysWithData} {daysWithData === 1 ? 'day' : 'days'} of data
                  </span>
                )}
              </p>
            </div>
          </div>
          {siteId && <CsvExportButton siteId={siteId} today={history.end_date} />}
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <SummaryTile
          label="Production"
          value={`${summary.total_production_kwh.toFixed(1)} kWh`}
          tone="solar"
        />
        <SummaryTile
          label="Consumption"
          value={`${summary.total_consumption_kwh.toFixed(1)} kWh`}
          tone="load"
        />
        <SummaryTile
          label="Imported"
          value={`${summary.total_import_kwh.toFixed(1)} kWh`}
          tone="import"
        />
        <SummaryTile
          label="Exported"
          value={`${summary.total_export_kwh.toFixed(1)} kWh`}
          tone="export"
        />
        <SummaryTile
          label="Net Cost"
          value={formatCurrency(summary.net_cost)}
          valueClass={netCostClass(summary.net_cost)}
          tone="cost"
        />
      </div>

      {/* Half-hour breakdown for the selected day */}
      {selectedDate && (
        <div ref={chartRef} className="space-y-2 scroll-mt-4">
          {dayError && (
            <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
              <AlertCircle className="w-4 h-4" />
              {dayError}
            </div>
          )}
          <EnergyFlowChart
            data={dayFlow}
            date={selectedDate}
            timezone={history.timezone ?? undefined}
            title={`Energy Flow · ${formatDate(selectedDate)}${
              selectedRow?.is_partial ? ' (today)' : ''
            }`}
            headerExtra={
              <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                {dayLoading && (
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-brand-orange" />
                )}
                <button
                  type="button"
                  onClick={() => olderDay && selectDay(olderDay.date)}
                  disabled={!olderDay}
                  aria-label="Previous day"
                  title={olderDay ? formatDate(olderDay.date) : undefined}
                  className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-brand-navy-light disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => newerDay && selectDay(newerDay.date)}
                  disabled={!newerDay}
                  aria-label="Next day"
                  title={newerDay ? formatDate(newerDay.date) : undefined}
                  className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-brand-navy-light disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            }
          />
        </div>
      )}

      {/* Daily totals table */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
          <Zap className="w-4 h-4 text-brand-orange" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Daily totals
          </h2>
          {days.length > 0 && (
            <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">
              {(summary.billed_days ?? 0) > 0 && (
                <span className="mr-3">
                  {summary.billed_days} of {days.length} days priced from Amber billing data;
                  the rest are SolanIQ estimates
                </span>
              )}
              Click a day for its half-hour breakdown
            </span>
          )}
        </div>
        {days.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
            No energy interval data has been recorded yet for this site.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-brand-navy-light text-xs uppercase text-gray-500 dark:text-gray-400">
                <tr>
                  <th className="px-4 py-3 text-left font-medium">Date</th>
                  <th className="px-4 py-3 text-right font-medium">Production</th>
                  <th className="px-4 py-3 text-right font-medium">Consumption</th>
                  <th className="px-4 py-3 text-right font-medium">Import</th>
                  <th className="px-4 py-3 text-right font-medium">Export</th>
                  <th className="px-4 py-3 text-right font-medium">Import $</th>
                  <th className="px-4 py-3 text-right font-medium">Export $</th>
                  <th className="px-4 py-3 text-right font-medium">Net Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {days.map((d) => {
                  const isSelected = d.date === selectedDate;
                  return (
                  <tr
                    key={d.date}
                    role="button"
                    tabIndex={0}
                    aria-pressed={isSelected}
                    onClick={() => selectDay(d.date)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        selectDay(d.date);
                      }
                    }}
                    className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-brand-navy-light focus:outline-none focus-visible:bg-gray-50 dark:focus-visible:bg-brand-navy-light ${
                      isSelected
                        ? 'bg-orange-50 dark:bg-brand-orange/10 border-l-2 border-brand-orange'
                        : 'border-l-2 border-transparent'
                    }`}
                    title={
                      d.is_partial
                        ? 'Today — totals so far, still updating'
                        : `Show half-hour breakdown for ${formatDate(d.date)}`
                    }
                  >
                    <td className="px-4 py-3 text-gray-900 dark:text-white whitespace-nowrap">
                      {formatDate(d.date)}
                      {d.is_partial && (
                        <span className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                          Today · partial
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-200">
                      {d.total_production_kwh.toFixed(1)} kWh
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-200">
                      {d.total_consumption_kwh.toFixed(1)} kWh
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-200">
                      {d.total_import_kwh.toFixed(1)} kWh
                    </td>
                    <td className="px-4 py-3 text-right text-gray-700 dark:text-gray-200">
                      {d.total_export_kwh.toFixed(1)} kWh
                    </td>
                    <td className="px-4 py-3 text-right text-red-600 dark:text-red-400">
                      ${d.total_import_cost.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right text-green-600 dark:text-green-400">
                      ${d.total_export_value.toFixed(2)}
                    </td>
                    <td
                      className={`px-4 py-3 text-right font-semibold ${netCostClass(d.net_cost)}`}
                    >
                      {formatCurrency(d.net_cost)}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Battery mode log */}
      <div className="card overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
          <BatteryIcon className="w-4 h-4 text-brand-orange" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Battery mode log
          </h2>
          <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">
            {recentModeEvents.length}{' '}
            {recentModeEvents.length === 1 ? 'event' : 'events'} · last{' '}
            {MODE_LOG_HOURS}h
          </span>
        </div>
        {recentModeEvents.length === 0 ? (
          <div className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
            No battery mode changes in the last {MODE_LOG_HOURS} hours.
          </div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-gray-700">
            {pagedModeEvents.map((event) => {
              const isAuto =
                event.source === 'auto' ||
                event.source === 'slot_target' ||
                event.source === 'price_guard';
              const isSuggestion = event.source === 'suggestion';
              const isExportLimit = event.event_type === 'site.export_limit';
              const Icon = isExportLimit
                ? SlidersHorizontal
                : isSuggestion
                  ? Lightbulb
                  : isAuto
                    ? Cpu
                    : UserIcon;
              const sourceLabel = isSuggestion
                ? 'Suggested'
                : event.source === 'price_guard'
                  ? 'Price guard'
                  : event.source === 'slot_target'
                    ? 'Slot target'
                    : isAuto
                      ? 'Auto'
                      : event.source === 'manual'
                        ? 'Manual'
                        : '—';
              const limitText =
                event.export_limit_percent != null
                  ? `${event.export_limit_percent}%${
                      event.export_limit_w != null ? ` (${Math.round(event.export_limit_w)}W)` : ''
                    }`
                  : null;
              const modeLabel = isExportLimit
                ? `Export limit ${limitText ?? ''}`.trim()
                : event.inverter_mode || event.mode || 'Mode change';
              const iconTone = isExportLimit
                ? 'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-400'
                : isSuggestion
                  ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400'
                  : isAuto
                    ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                    : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400';
              return (
                <li key={event.id} className="px-6 py-3 flex items-start gap-3">
                  <div className={`mt-0.5 p-1.5 rounded-md ${iconTone}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline justify-between gap-2 flex-wrap">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {modeLabel}
                      </p>
                      <span className="text-xs text-gray-400 dark:text-gray-500">
                        {formatTimestamp(event.timestamp)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {event.message || ''}
                    </p>
                    <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-400 dark:text-gray-500">
                      <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-brand-navy-light">
                        {sourceLabel}
                      </span>
                      {event.actor_email && (
                        <span className="truncate">by {event.actor_email}</span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {totalPages > 1 && (
          <div className="px-6 py-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>
              Page {safePage + 1} of {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => setModeLogPage((p) => Math.max(0, p - 1))}
                disabled={safePage === 0}
                aria-label="Previous page"
                className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-brand-navy-light disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setModeLogPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={safePage >= totalPages - 1}
                aria-label="Next page"
                className="p-1 rounded-md hover:bg-gray-100 dark:hover:bg-brand-navy-light disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* EV charging sessions (only when the site has a charger) */}
      {evSessions !== null && (
        <div className="card overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700 flex items-center gap-2">
            <PlugZap className="w-4 h-4 text-brand-orange" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              EV charging sessions
            </h2>
            <span className="ml-auto text-xs text-gray-400 dark:text-gray-500">
              {evChargerName} · {evSessions.length}{' '}
              {evSessions.length === 1 ? 'session' : 'sessions'} · last {EV_SESSION_DAYS} days
            </span>
          </div>
          {evSessions.length === 0 ? (
            <div className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
              No charging sessions in the last {EV_SESSION_DAYS} days.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 dark:bg-brand-navy-light/50">
                  <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
                    <th className="px-6 py-2 font-medium">Started</th>
                    <th className="px-6 py-2 font-medium">Duration</th>
                    <th className="px-6 py-2 font-medium">Energy</th>
                    <th className="px-6 py-2 font-medium">Source</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {evSessions.map((sess) => (
                    <tr key={sess.id}>
                      <td className="px-6 py-2 text-gray-900 dark:text-white whitespace-nowrap">
                        {parseUtc(sess.started_at).toLocaleDateString('en-AU', {
                          day: 'numeric',
                          month: 'short',
                        })}{' '}
                        <span className="text-gray-400">
                          {parseUtc(sess.started_at).toLocaleTimeString('en-AU', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: false,
                          })}
                        </span>
                      </td>
                      <td className="px-6 py-2 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {formatSessionDuration(sess.started_at, sess.ended_at)}
                      </td>
                      <td
                        className="px-6 py-2 text-gray-900 dark:text-white whitespace-nowrap"
                        title={sess.source === 'manual' ? 'Estimated from site load' : undefined}
                      >
                        {sess.source === 'manual' ? '~' : ''}
                        {(sess.energy_wh / 1000).toFixed(1)} kWh
                      </td>
                      <td className="px-6 py-2 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-brand-navy-light">
                          {sess.source === 'manual' ? 'Declared' : 'OCPP'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function shiftDate(yyyymmdd: string, days: number): string {
  const [y, m, d] = yyyymmdd.split('-').map((s) => Number.parseInt(s, 10));
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

const fieldClass =
  'px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-input text-gray-900 dark:text-white focus:ring-2 focus:ring-brand-orange focus:border-transparent';

/**
 * Small header button that opens a dialog to download recorded data as CSV
 * for a range of site-local days. `today` is the site-local date from the
 * history response, so the picker's limits match the site's day rather
 * than the browser's.
 */
function CsvExportButton({ siteId, today }: { siteId: string; today: string }) {
  const yesterday = shiftDate(today, -1);
  const [open, setOpen] = useState(false);
  const [start, setStart] = useState(yesterday);
  const [end, setEnd] = useState(yesterday);
  const [kind, setKind] = useState<CsvExportKind>('readings');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy]);

  const close = () => {
    if (!busy) setOpen(false);
  };

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      await downloadSiteCsv(siteId, start, end, kind);
      setOpen(false);
    } catch (err) {
      console.error('CSV export failed:', err);
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
        className="px-3 py-1.5 text-sm font-medium text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full flex items-center gap-1"
        title="Export CSV"
      >
        <Download className="w-4 h-4" />
        Export
      </button>
      {open && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={close}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="csv-export-title"
            className="card shadow-xl w-full max-w-md mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2
                id="csv-export-title"
                className="text-lg font-semibold text-gray-900 dark:text-white"
              >
                Export CSV
              </h2>
              <button onClick={close} className="text-gray-400 hover:text-gray-500" title="Close">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <label className="flex flex-col gap-1 text-xs text-gray-500 dark:text-gray-400">
                  From
                  <input
                    type="date"
                    value={start}
                    max={today}
                    onChange={(e) => {
                      setStart(e.target.value);
                      if (e.target.value > end) setEnd(e.target.value);
                    }}
                    className={fieldClass}
                  />
                </label>
                <label className="flex flex-col gap-1 text-xs text-gray-500 dark:text-gray-400">
                  To
                  <input
                    type="date"
                    value={end}
                    min={start}
                    max={today}
                    onChange={(e) => setEnd(e.target.value)}
                    className={fieldClass}
                  />
                </label>
              </div>
              <label className="flex flex-col gap-1 text-xs text-gray-500 dark:text-gray-400">
                Data
                <select
                  value={kind}
                  onChange={(e) => setKind(e.target.value as CsvExportKind)}
                  className={fieldClass}
                >
                  <option value="readings">5-minute readings</option>
                  <option value="daily">Daily totals</option>
                </select>
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {kind === 'readings'
                  ? 'One row per reading (about every 5 minutes): kWh for the interval since the previous reading, plus the power at that moment. Up to 92 days.'
                  : 'One row per day in kWh, including battery charge and discharge. Up to 366 days.'}
              </p>
              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={close}
                disabled={busy}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={download}
                disabled={busy || !start || !end}
                className="px-4 py-2 text-sm font-medium text-white bg-brand-orange hover:bg-brand-orange/90 rounded-lg disabled:opacity-50 flex items-center gap-1"
              >
                {busy ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                Download
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

interface SummaryTileProps {
  label: string;
  value: string;
  valueClass?: string;
  tone?: 'solar' | 'load' | 'import' | 'export' | 'cost';
}

function SummaryTile({ label, value, valueClass, tone }: SummaryTileProps) {
  const toneRing: Record<string, string> = {
    solar: 'border-blue-200 dark:border-blue-800',
    load: 'border-amber-200 dark:border-amber-800',
    import: 'border-red-200 dark:border-red-800',
    export: 'border-green-200 dark:border-green-800',
    cost: 'border-gray-200 dark:border-gray-700',
  };
  return (
    <div
      className={`bg-card dark:bg-brand-navy rounded-xl shadow-sm border p-4 ${
        toneRing[tone || 'cost'] || 'border-gray-200 dark:border-gray-700'
      }`}
    >
      <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </p>
      <p
        className={`mt-1 text-xl font-bold text-gray-900 dark:text-white ${
          valueClass || ''
        }`}
      >
        {value}
      </p>
    </div>
  );
}
